import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "./baseQuery";
import { API_SERVER } from "../utils/urlBack_End";

export const propuestasApi = createApi({
  reducerPath: "propuestasApi",
  baseQuery: baseQuery(`${API_SERVER}/propuestas`),
  tagTypes: [
    "Propuestas",
    "PlanesPropuesta",
    "NotificacionesPropuestas",
    "PropuestaV2Public",
    "PropuestaTracking",
    "HiloNegociacion",
  ],
  endpoints: (builder) => ({
    getPropuestasSucursales: builder.query({
      query: ({ sucursal, tipoPropuesta }) =>
        `/sucursales/${sucursal}/${tipoPropuesta}`,
      providesTags: ["Propuestas"],
    }),
    getPropuestasFiltradasPorEstado: builder.query({
      query: ({ rut, estado, sucursales, fechaInicio, fechaFin }) => {
        const params = { rut, estado };

        if (sucursales && sucursales.length > 0) {
          params.sucursales = JSON.stringify(sucursales);
        }

        if (fechaInicio) {
          params.fechaInicio = fechaInicio;
        }

        if (fechaFin) {
          params.fechaFin = fechaFin;
        }

        return {
          url: `/propuestasFiltradas`,
          method: "GET",
          params,
        };
      },
    }),
    getResumenPorEstadoYSucursal: builder.query({
      query: ({ rut, fecha, fechaInicio, fechaFin, sucursales }) => {
        const params = { fecha };

        if (sucursales && sucursales.length > 0) {
          params.sucursales = JSON.stringify(sucursales);
        }

        if (fechaInicio) {
          params.fechaInicio = fechaInicio;
        }

        if (fechaFin) {
          params.fechaFin = fechaFin;
        }

        return {
          url: `/resumenPorEstadoYSucursal/${rut}`,
          method: "GET",
          params,
        };
      },
    }),
    obtenerPropuestaCliente: builder.query({
      query: ({ token }) => ({
        url: "/ver",
        method: "GET",
        extra: { token },
      }),
    }),
    trackingPropuesta: builder.query({
      query: ({ id }) => ({
        url: `/${id}/tracking`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    generarOTP: builder.mutation({
      query: ({ token }) => ({
        url: "/generarOTP",
        method: "POST",
        body: {},
        extra: { token },
      }),
    }),
    verificarOTP: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/verificarOTP",
        method: "POST",
        body,
        extra: { token },
      }),
      invalidatesTags: ["Propuestas"],
    }),
    rechazarPropuesta: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/rechazar",
        method: "POST",
        body,
        extra: { token },
      }),
      invalidatesTags: ["Propuestas"],
    }),
    enviarPropuestaCliente: builder.mutation({
      query: ({ id, ...body }) => {
        return {
          url: `/${id}/enviar`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    reenviarPropuestaCliente: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/${id}/reenviar`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    extenderPropuesta: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/${id}/extender`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    reactivarPropuesta: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/${id}/reactivar`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    getPropuestasPorSucursalYEstado: builder.query({
      query: ({ sucursal, estado, fecha }) => {
        return {
          url: `/propuestasPorSucursalYEstado/${sucursal}/${estado}`,
          method: "GET",
          params: {
            fecha,
          },
        };
      },
    }),
    actualizarServicioPropuestoTareaSegunID: builder.mutation({
      query: ({ idPropuesta, ...body }) => ({
        url: `/actualizarServicioPropuestoTareaSegunID/${idPropuesta}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    agregarSubtareaPropuestaTarea: builder.mutation({
      query: ({ idPropuesta, ...body }) => ({
        url: `/agregarSubtareaPropuestaTarea/${idPropuesta}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    eliminarSubtareaPropuestaTarea: builder.mutation({
      query: ({ idSubtareaPropuesta }) => ({
        url: `/eliminarSubtareaPropuestaTarea/${idSubtareaPropuesta}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Propuestas"],
    }),
    getPropuestasEmpleado: builder.query({
      query: ({ usuario, id, tipo }) => `/empleado/${usuario}/${id}/${tipo}`,
      providesTags: ["Propuestas"],
    }),
    getPropuesta: builder.query({
      query: ({ id, tipo }) => `/${tipo}/${id}`,
      providesTags: ["Propuestas"],
    }),
    addPropuesta: builder.mutation({
      query: (body) => ({
        url: `/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    actualizarSolicitantePropuesta: builder.mutation({
      query: (body) => ({
        url: `/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    addPropuestaTareaEspecial: builder.mutation({
      query: (body) => ({
        url: `/crearPropuestaTareaEspecial`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    deletePropuesta: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Propuestas"],
    }),
    updateEstadoPropuesta: builder.mutation({
      query: ({ id, tipo, body }) => ({
        url: `/estado/${tipo}/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    updateDatosPropuesta: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/datosPropuesta/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    actualizarArchivoPropuesta: builder.mutation({
      query: ({ idPropuesta, ...body }) => ({
        url: `/actualizarArchivoPropuesta/${idPropuesta}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    actualizarDatosCliente: builder.mutation({
      query: ({ idPropuesta, ...body }) => ({
        url: `/actualizarDatosCliente/${idPropuesta}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    actualizarDatosClienteTareaEspecial: builder.mutation({
      query: ({ idPropuesta, ...body }) => ({
        url: `/actualizarDatosClienteTareaEspecial/${idPropuesta}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    obtenerPlanesPropuesta: builder.query({
      query: (idPropuesta) => `/relacionPlan/${idPropuesta}`,
      providesTags: ["PlanesPropuesta"],
    }),
    obtenerServicios: builder.query({
      query: ({ id, tipo }) => `/relacionPlan/exportar/${tipo}/${id}`,
    }),
    agregarPlanPropuesta: builder.mutation({
      query: (body) => ({
        url: `/relacionPlan`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PlanesPropuesta"],
    }),
    editarPlanPropuesta: builder.mutation({
      query: (data) => ({
        url: `/relacionPlan/editar`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["PlanesPropuesta"],
    }),
    eliminarPlanPropuesta: builder.mutation({
      query: ({ idPropuesta, tipoPlan, rutCliente }) => ({
        url: `/relacionPlan/${idPropuesta}/${tipoPlan}/${rutCliente}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PlanesPropuesta"],
    }),
    duplicarPropuesta: builder.mutation({
      query: ({ id, sucursal }) => ({
        url: `/${id}/duplicar`,
        method: "POST",
        body: sucursal ? { sucursal } : {},
      }),
      // Invalida tracking del padre para reflejar el evento 'clonada'
      // emitido por el helper clonarPropuestaV2 desde el backend.
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    crearPropuestaDesdeRechazados: builder.mutation({
      query: ({ id, sucursal }) => ({
        url: `/v2/${id}/desde-rechazados`,
        method: "POST",
        body: sucursal ? { sucursal } : {},
      }),
      // Invalida tracking del padre para reflejar el evento 'clonada'
      // con metadata.tipo_origen = 'DESDE_RECHAZADOS'.
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
      ],
    }),
    getNotificacionesPropuestas: builder.query({
      query: (rut) => `/notificaciones/${rut}`,
      providesTags: ["NotificacionesPropuestas"],
    }),
    getPropuestasCliente: builder.query({
      query: ({ rutCliente, fechaInicio, fechaFin, estado }) => {
        const params = {};

        if (fechaInicio && fechaFin) {
          params.fechaInicio = fechaInicio;
          params.fechaFin = fechaFin;
        }

        if (estado) {
          params.estado = estado;
        }

        return {
          url: `/cliente/${rutCliente}`,
          method: "GET",
          params,
        };
      },
      providesTags: ["Propuestas"],
    }),

    // ── v2 ──────────────────────────────────────────────────────────────────
    descargarPDFPropuestaV2: builder.mutation({
      query: ({ id }) => ({
        url: `/v2/${id}/pdf`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
    previewPDFPropuestaV2: builder.mutation({
      query: (body) => ({
        url: `/v2/preview-pdf`,
        method: "POST",
        body,
        responseHandler: (response) => response.blob(),
      }),
    }),
    addPropuestaV2: builder.mutation({
      query: (body) => ({
        url: `/v2`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    editarPropuestaV2: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/v2/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    getPropuestaV2: builder.query({
      query: ({ id }) => `/v2/${id}`,
      providesTags: ["Propuestas"],
    }),
    buscarSolicitantes: builder.query({
      query: ({ q }) => ({
        url: `/solicitantes`,
        method: "GET",
        params: { q },
      }),
    }),
    editarSolicitante: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/solicitantes/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Propuestas"],
    }),
    usoSolicitante: builder.query({
      query: (id) => ({ url: `/solicitantes/${id}/uso`, method: "GET" }),
    }),
    getPropuestaV2Public: builder.query({
      query: ({ token }) => ({
        url: "/v2/ver",
        method: "GET",
        extra: { token },
      }),
      providesTags: (result, error, arg) => [
        { type: "PropuestaV2Public", id: arg.token },
      ],
    }),
    responderPropuestaV2: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/v2/responder",
        method: "POST",
        body,
        extra: { token },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "PropuestaV2Public", id: arg.token },
      ],
    }),
    // registrarObservacionInternaV2 replica esta misma definición bajo otro
    // nombre. Si la ruta, el método o las invalidaciones cambian aquí sin
    // cambiar también allá, una de las dos vistas queda llamando a una
    // acción distinta de la que el usuario ve en pantalla, o deja de
    // refrescar el tracking y el hilo después de guardar.
    aceptarInternaV2: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/v2/${id}/aceptar-interna`,
        method: "POST",
        body: formData,
      }),
      // Invalida también el tracking y el hilo para que el historial de
      // eventos se refresque con la aceptación recién registrada sin
      // recargar la página.
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
        { type: "HiloNegociacion", id: arg.id },
      ],
    }),
    // Segunda definicion sobre la misma ruta que aceptarInternaV2, con el mismo
    // cuerpo (FormData). No es un alias: son dos endpoints declarados, y existen
    // los dos porque este codigo base tambien accede a los hooks por el
    // namespace del servicio, donde un alias a nivel de export no seria
    // alcanzable. El nombre distinto es el punto: ahi no se esta "aceptando"
    // sino registrando lo que el cliente pidio. Si cambia la ruta o las
    // invalidaciones de una, hay que cambiar la otra.
    registrarObservacionInternaV2: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/v2/${id}/aceptar-interna`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result, error, arg) => [
        "Propuestas",
        { type: "PropuestaTracking", id: arg.id },
        { type: "HiloNegociacion", id: arg.id },
      ],
    }),
    editarItemsRenegociacionV2: builder.mutation({
      query: ({ id, body }) => ({
        url: `/v2/${id}/items-renegociacion`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Propuestas",
        { type: "HiloNegociacion", id },
      ],
    }),
    enviarRondaRenegociacionV2: builder.mutation({
      query: ({ id, body }) => ({
        url: `/v2/${id}/enviar-ronda`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Propuestas",
        { type: "HiloNegociacion", id },
        // Esta mutación escribe un evento de seguimiento, y la línea de tiempo
        // que lo muestra vive en la misma pantalla: sin invalidar su tag, el
        // evento recién escrito no aparece hasta que alguien recargue.
        { type: "PropuestaTracking", id },
      ],
    }),
    cerrarPropuestaV2: builder.mutation({
      query: ({ id, body }) => ({
        url: `/v2/${id}/cerrar`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Propuestas",
        { type: "HiloNegociacion", id },
        // Esta mutación escribe un evento de seguimiento, y la línea de tiempo
        // que lo muestra vive en la misma pantalla: sin invalidar su tag, el
        // evento recién escrito no aparece hasta que alguien recargue.
        { type: "PropuestaTracking", id },
      ],
    }),
    getHiloNegociacionV2: builder.query({
      query: ({ id }) => `/v2/${id}/hilo`,
      providesTags: (result, error, { id }) => [
        { type: "HiloNegociacion", id },
      ],
    }),

    // ── IA ──────────────────────────────────────────────────────────────────
    mejorarTexto: builder.mutation({
      query: (body) => ({
        url: `/mejorarTexto`,
        method: "POST",
        body,
      }),
    }),
    generarResumenEjecutivo: builder.mutation({
      query: (body) => ({
        url: `/generarResumenEjecutivo`,
        method: "POST",
        body,
      }),
    }),
    generarProximosPasos: builder.mutation({
      query: (body) => ({
        url: `/generarProximosPasos`,
        method: "POST",
        body,
      }),
    }),
    generarConIA: builder.mutation({
      query: (body) => ({
        url: `/generarConIA`,
        method: "POST",
        body,
      }),
    }),
    generarBorradorDesdeMigracion: builder.mutation({
      query: (body) => ({
        url: `/generarBorradorDesdeMigracion`,
        method: "POST",
        body,
      }),
    }),
    migrarPropuestaV1: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/${id}/migrar`,
        method: "POST",
      }),
      invalidatesTags: ["Propuestas"],
    }),
    transcribirAudio: builder.mutation({
      query: (formData) => ({
        url: `/transcribirAudio`,
        method: "POST",
        body: formData,
      }),
    }),
    getListadoUnificado: builder.query({
      query: ({ sucursales } = {}) => {
        const params = {};
        if (sucursales && sucursales.length > 0) {
          params.sucursales = JSON.stringify(sucursales);
        }
        return {
          url: `/listadoUnificado`,
          method: "GET",
          params,
        };
      },
      providesTags: ["Propuestas"],
    }),
  }),
});

export const {
  useGetNotificacionesPropuestasQuery,
  useLazyGetNotificacionesPropuestasQuery,
  useGetPropuestasEmpleadoQuery,
  useGetPropuestasSucursalesQuery,
  useGetPropuestasPorSucursalYEstadoQuery,
  useGetPropuestaQuery,
  useGetPropuestasFiltradasPorEstadoQuery,
  useGetResumenPorEstadoYSucursalQuery,
  useEnviarPropuestaClienteMutation,
  useReenviarPropuestaClienteMutation,
  useExtenderPropuestaMutation,
  useReactivarPropuestaMutation,
  useDuplicarPropuestaMutation,
  useCrearPropuestaDesdeRechazadosMutation,
  useObtenerPlanesPropuestaQuery,
  useObtenerPropuestaClienteQuery,
  useActualizarSolicitantePropuestaMutation,
  useGenerarOTPMutation,
  useVerificarOTPMutation,
  useRechazarPropuestaMutation,
  useObtenerServiciosQuery,
  useTrackingPropuestaQuery,
  useLazyGetPropuestasFiltradasPorEstadoQuery,
  useLazyGetResumenPorEstadoYSucursalQuery,
  useLazyTrackingPropuestaQuery,
  useLazyObtenerServiciosQuery,
  useLazyObtenerPlanesPropuestaQuery,
  useLazyObtenerPropuestaClienteQuery,
  useLazyGetPropuestasPorSucursalYEstadoQuery,
  useLazyGetPropuestasEmpleadoQuery,
  useLazyGetPropuestasSucursalesQuery,
  useLazyGetPropuestaQuery,
  useDeletePropuestaMutation,
  useAddPropuestaMutation,
  useAddPropuestaTareaEspecialMutation,
  useUpdateEstadoPropuestaMutation,
  useActualizarServicioPropuestoTareaSegunIDMutation,
  useActualizarArchivoPropuestaMutation,
  useUpdateDatosPropuestaMutation,
  useActualizarDatosClienteTareaEspecialMutation,
  useActualizarDatosClienteMutation,
  useAgregarPlanPropuestaMutation,
  useEliminarPlanPropuestaMutation,
  useEliminarSubtareaPropuestaTareaMutation,
  useAgregarSubtareaPropuestaTareaMutation,
  useEditarPlanPropuestaMutation,
  useGetPropuestasClienteQuery,
  useLazyGetPropuestasClienteQuery,
  useDescargarPDFPropuestaV2Mutation,
  usePreviewPDFPropuestaV2Mutation,
  useAddPropuestaV2Mutation,
  useEditarPropuestaV2Mutation,
  useGetPropuestaV2Query,
  useLazyGetPropuestaV2Query,
  useGetPropuestaV2PublicQuery,
  useLazyGetPropuestaV2PublicQuery,
  useResponderPropuestaV2Mutation,
  useAceptarInternaV2Mutation,
  useRegistrarObservacionInternaV2Mutation,
  useEditarItemsRenegociacionV2Mutation,
  useEnviarRondaRenegociacionV2Mutation,
  useCerrarPropuestaV2Mutation,
  useGetHiloNegociacionV2Query,
  useLazyBuscarSolicitantesQuery,
  useEditarSolicitanteMutation,
  useLazyUsoSolicitanteQuery,
  useMejorarTextoMutation,
  useGenerarResumenEjecutivoMutation,
  useGenerarProximosPasosMutation,
  useGenerarConIAMutation,
  useGenerarBorradorDesdeMigracionMutation,
  useMigrarPropuestaV1Mutation,
  useTranscribirAudioMutation,
  useGetListadoUnificadoQuery,
  useLazyGetListadoUnificadoQuery,
} = propuestasApi;
